import { forwardRef, useMemo, useRef, useState } from "react";
import { ChevronDown } from "lucide-react";
import useClickOutside from "../../../_hooks/useClickOutside";
import { countryCodes } from "./countryCodes";
import "./_phone-input.scss";

function parseValue(value) {
  const [dialCode = "", ...rest] = (value ?? "").split(" ");
  return { dialCode, phoneNumber: rest.join(" ") };
}

// Session-lifetime memory of which iso2 was explicitly clicked for a given
// composed "<dialCode> <phoneNumber>" value - keyed by the exact wire value
// so a PhoneInput that remounts with data written earlier this session (the
// Create form's instance, then a later Edit form's fresh instance) recovers
// the real country instead of falling back to the alphabetically-first
// same-dial-code match (see D3 below - the concrete repro: create with
// "United States" +1, reopen Edit, the trigger showed "Canada" +1 instead).
// Module-scope, not component state, specifically so it outlives any one
// instance. Doesn't survive a full page reload (fresh module, empty Map) -
// the wire value itself only ever carries a dial code, not a country
// identity, so that case needs a wire-format change no caller/backend
// expects today, not a fix that belongs in this component alone.
const recentCountryByValue = new Map();

/**
 * Pure-UI phone input: a country/dial-code picker (static list, no network
 * call - see countryCodes.js) + a plain number field. `value` is a single
 * "<dialCode> <phoneNumber>" string, matching what onboarding's form data
 * already parses via .split(" ") - a drop-in value shape for existing callers.
 */
const PhoneInput = forwardRef(function PhoneInput(
  {
    id = "phone-input-number",
    label = "Phone number",
    value,
    onChange,
    onBlur,
    error,
    disabled = false,
    required = false,
    placeholder = "",
    className = "",
    ...rest
  },
  ref
) {
  const [isOpen, setIsOpen] = useState(false);
  const [search, setSearch] = useState("");
  // D3 - `value` only ever carries a dial code, not a country identity, so
  // several genuinely different countries sharing one dial code (US/Canada
  // both root "+1", among others - see countryCodes.js's own comment) are
  // indistinguishable from `value` alone. Looking the country up by dial
  // code alone (the old behavior) always resolved to whichever one sorts
  // first by name ("Canada" before "United States"), so the trigger button
  // stayed visually stuck on that one country no matter which was actually
  // clicked. Remembering the explicitly clicked iso2 here disambiguates the
  // display without changing `value`'s own "<dialCode> <phoneNumber>" shape
  // (existing callers/backends still only ever see the dial code) - falls
  // back to the old by-dial-code lookup whenever nothing's been explicitly
  // clicked yet (initial render, or an externally-set `value` whose dial
  // code no longer matches the remembered country).
  const [selectedIso2, setSelectedIso2] = useState(undefined);
  const dropdownRef = useRef(null);

  const { dialCode, phoneNumber } = parseValue(value);
  const selectedCountry =
    countryCodes.find((c) => c.iso2 === selectedIso2 && c.dialCode === dialCode) ||
    countryCodes.find((c) => c.iso2 === recentCountryByValue.get(value) && c.dialCode === dialCode) ||
    countryCodes.find((c) => c.dialCode === dialCode);

  useClickOutside(dropdownRef, () => setIsOpen(false));

  const filteredCountries = useMemo(() => {
    const term = search.trim().toLowerCase();
    if (!term) return countryCodes;
    return countryCodes.filter(
      (c) =>
        c.name.toLowerCase().includes(term) || c.dialCode.includes(term)
    );
  }, [search]);

  const composeValue = (nextDialCode, nextPhoneNumber, iso2) => {
    // Deliberately not trimmed: parseValue splits on the first space to
    // recover {dialCode, phoneNumber}, so the delimiter must survive even
    // when one side is still empty (e.g. digits typed before a country is
    // picked) - trimming would collapse " 9876543210" into "9876543210"
    // and the next parse would misread the phone number as the dial code.
    if (!nextDialCode && !nextPhoneNumber) {
      onChange?.("");
      return;
    }
    const nextValue = `${nextDialCode} ${nextPhoneNumber}`;
    if (iso2) recentCountryByValue.set(nextValue, iso2);
    onChange?.(nextValue);
  };

  const handleSelectCountry = (country) => {
    composeValue(country.dialCode, phoneNumber, country.iso2);
    setSelectedIso2(country.iso2);
    setIsOpen(false);
    setSearch("");
  };

  const handlePhoneNumberChange = (e) => {
    // Re-cache on every keystroke, not just at the moment a country is
    // picked - handleSelectCountry's own cache write happens before the
    // digits typed afterward exist, so it caches "<dialCode> " (empty
    // phone), never the final value that actually gets saved. Passing the
    // still-current selectedIso2 here keeps the cache entry pointed at
    // whatever the trigger is actually showing, all the way to the final
    // composed value.
    composeValue(dialCode, e.target.value, selectedIso2);
  };

  return (
    <div
      className={`phone-input ${error ? "phone-input--error" : ""} ${className}`}
      ref={dropdownRef}
    >
      {label && (
        <label className="phone-input__label" htmlFor={id}>
          {required ? `${label} *` : label}
        </label>
      )}
      <div className="phone-input__field-wrapper">
        <button
          type="button"
          className="phone-input__country-trigger"
          onClick={() => !disabled && setIsOpen((prev) => !prev)}
          disabled={disabled}
        >
          {selectedCountry ? (
            <>
              <span aria-hidden="true">{selectedCountry.flag}</span>
              <span>{selectedCountry.dialCode}</span>
            </>
          ) : (
            <span className="phone-input__placeholder">Code</span>
          )}
          <ChevronDown size={14} />
        </button>

        <input
          ref={ref}
          id={id}
          type="tel"
          className="phone-input__number-field"
          value={phoneNumber}
          placeholder={placeholder}
          disabled={disabled}
          autoComplete="off"
          onChange={handlePhoneNumberChange}
          onBlur={onBlur}
          {...rest}
        />

        {isOpen && (
          <div className="phone-input__dropdown">
            <div className="phone-input__search-row">
              <input
                type="text"
                className="phone-input__search"
                placeholder="Search country or code"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                autoFocus
              />
            </div>
            <ul className="phone-input__country-list">
              {filteredCountries.map((country) => (
                <li key={country.iso2}>
                  <button
                    type="button"
                    className="phone-input__country-option"
                    onClick={() => handleSelectCountry(country)}
                  >
                    <span aria-hidden="true">{country.flag}</span>
                    <span className="phone-input__country-name">
                      {country.name}
                    </span>
                    <span className="phone-input__country-dial-code">
                      {country.dialCode}
                    </span>
                  </button>
                </li>
              ))}
              {filteredCountries.length === 0 && (
                <li className="phone-input__empty">No matches</li>
              )}
            </ul>
          </div>
        )}
      </div>
      {error && <p className="phone-input__error">{error}</p>}
    </div>
  );
});

export default PhoneInput;
